import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from "../ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
    CardFooter,
} from "../ui/card";
import { Textarea } from "../ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../ui/tooltip";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { AlertCircle, Copy, Check } from "lucide-react";
import { cn } from "../../lib/utils";
import { useApi } from "../../lib/ApiContext";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Switch } from "../ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

// LLM models options - OpenAI models only for marketing landing pages
const llmModelOptions = [
    { value: "gpt-4o", label: "GPT-4o - Enhanced creative writing" },
    { value: "gpt-4o-mini", label: "GPT-4o Mini - Fast and cost-effective" },
    { value: "gpt-4.1", label: "GPT-4.1 - Latest flagship model" },
    { value: "gpt-4.1-mini", label: "GPT-4.1 Mini - Fast and efficient" },
    { value: "gpt-5.6", label: "GPT-5.6 - Latest model" },
    { value: "gpt-5.5", label: "GPT-5.5 - Previous generation" },
    { value: "o3", label: "OpenAI o3 - Advanced reasoning" },
    { value: "o4-mini", label: "OpenAI o4 Mini - Fast and efficient" },
    { value: "gpt-4.1-nano", label: "GPT-4.1 Nano - Fast and efficient" },
];

// Temperature options
const temperatureOptions = [
    { value: 0.0, label: "0.0 - Very deterministic" },
    { value: 0.1, label: "0.1" },
    { value: 0.2, label: "0.2" },
    { value: 0.3, label: "0.3" },
    { value: 0.4, label: "0.4" },
    { value: 0.5, label: "0.5 - Balanced" },
    { value: 0.6, label: "0.6" },
    { value: 0.7, label: "0.7" },
    { value: 0.8, label: "0.8" },
    { value: 0.9, label: "0.9" },
    { value: 1.0, label: "1.0" },
    { value: 1.1, label: "1.1" },
    { value: 1.2, label: "1.2 - Very creative" },
];

const TemplateEdit = () => {
    const { orgPublicId, templatePublicId } = useParams();
    const navigate = useNavigate();
    const api = useApi();

    const [organization, setOrganization] = useState(null);
    const [template, setTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generalError, setGeneralError] = useState(null);
    const [isActive, setIsActive] = useState(true);
    const [autoCreatePages, setAutoCreatePages] = useState(false);
    const [noindex, setNoindex] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        llm_model: '',
        temperature: 0.7,
        daily_rate_limit: 2000,
        llm_context: '',
        llm_prompt: '',
        master_html_url: '',
        template_path: '',
        html_selectors: [],
        dynamic_selector_prefix: '',
        auto_create_pages: false,
        brand_voice_origin_sites: '',
        noindex: false,
    });
    const [fieldErrors, setFieldErrors] = useState({});
    const [newSelector, setNewSelector] = useState({
        name: '',
        selector_path: ''
    });
    const [selectorMode, setSelectorMode] = useState('manual');
    const [isSaving, setIsSaving] = useState(false);
    const [isPurgingCache, setIsPurgingCache] = useState(false);
    const [purgeCacheSuccessMessage, setPurgeCacheSuccessMessage] = useState(null);
    const [purgeCacheErrorMessage, setPurgeCacheErrorMessage] = useState(null);
    const [brandVoiceStore, setBrandVoiceStore] = useState(null);
    const [originSites, setOriginSites] = useState([]);
    const [isBrandVoiceLoading, setIsBrandVoiceLoading] = useState(false);
    const [copiedField, setCopiedField] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch organization data
                const org = await api.organizations.getByPublicId(orgPublicId);
                if (org) {
                    setOrganization(org);

                    // Fetch template data
                    try {
                        const foundTemplate = await api.templates.getByPublicId(templatePublicId);
                        if (foundTemplate) {
                            setTemplate(foundTemplate);
                            const templateData = {
                                name: foundTemplate.name,
                                llm_model: foundTemplate.llm_model,
                                temperature: foundTemplate.temperature || 0.7,
                                daily_rate_limit: foundTemplate.daily_rate_limit || 2000,
                                llm_context: foundTemplate.llm_context || '',
                                llm_prompt: foundTemplate.llm_prompt || '',
                                master_html_url: foundTemplate.master_html_url || '',
                                template_path: foundTemplate.template_path || '',
                                html_selectors: foundTemplate.html_selectors || [],
                                dynamic_selector_prefix: foundTemplate.dynamic_selector_prefix || '',
                                auto_create_pages: foundTemplate.auto_create_pages || false,
                                brand_voice_origin_sites: foundTemplate.brand_voice_origin_sites || '',
                                organization_id: org.id,
                                noindex: foundTemplate.noindex || false
                            };
                            setFormData(templateData);

                            // If dynamic selector prefix exists, switch to dynamic mode
                            if (templateData.dynamic_selector_prefix && templateData.dynamic_selector_prefix.trim() !== '') {
                                setSelectorMode('dynamic');
                            }

                            // Set active state from template data, default to false if not specified
                            setIsActive(foundTemplate.active !== undefined ? foundTemplate.active : false);
                            // Set auto create pages state from template data, default to false if not specified
                            setAutoCreatePages(foundTemplate.auto_create_pages !== undefined ? foundTemplate.auto_create_pages : false);
                            // Set noindex state from template data, default to false if not specified
                            setNoindex(foundTemplate.noindex !== undefined ? Boolean(foundTemplate.noindex) : false);
                        } else {
                            // Template not found - set this as a general error but don't use the error state
                            setGeneralError("Template not found");
                            // Navigate back to organization after a short delay
                            setTimeout(() => {
                                navigate(`/organizations/${orgPublicId}/edit`);
                            }, 2000);
                        }
                    } catch (err) {
                        console.error("Failed to fetch template:", err);
                        setGeneralError(err.message);
                    }

                    // Fetch brand voice data for this organization
                    try {
                        setIsBrandVoiceLoading(true);
                        const brandVoiceStoreData = await api.organizations.getBrandVoiceStore(org.id);
                        setBrandVoiceStore(brandVoiceStoreData);

                        // If we have a brand voice store, fetch origin sites
                        if (brandVoiceStoreData && brandVoiceStoreData.openai_vector_store_id) {
                            try {
                                const originSitesData = await api.serviceBindings.listOriginSites(brandVoiceStoreData.openai_vector_store_id);
                                if (originSitesData && originSitesData.success && originSitesData.origin_sites) {
                                    setOriginSites(originSitesData.origin_sites);
                                } else {
                                    setOriginSites([]);
                                }
                            } catch (err) {
                                console.error("Failed to fetch origin sites:", err);
                                setOriginSites([]);
                            }
                        } else {
                            setOriginSites([]);
                        }
                    } catch (err) {
                        console.error("Failed to fetch brand voice store:", err);
                        setBrandVoiceStore(null);
                        setOriginSites([]);
                    } finally {
                        setIsBrandVoiceLoading(false);
                    }
                } else {
                    // Organization not found - set this as a general error but don't use the error state
                    setGeneralError("Organization not found");
                    // Navigate back to organizations after a short delay
                    setTimeout(() => {
                        navigate('/organizations');
                    }, 2000);
                }
            } catch (err) {
                console.error("Failed to fetch organization:", err);
                setGeneralError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [orgPublicId, templatePublicId, navigate, api.organizations, api.templates]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Special handling for daily_rate_limit to ensure it's an integer
        if (name === 'daily_rate_limit') {
            // Convert to integer and ensure it's not negative
            const intValue = parseInt(value, 10);
            setFormData({
                ...formData,
                [name]: isNaN(intValue) ? '' : Math.max(0, intValue)
            });
        } else {
            setFormData({
                ...formData,
                [name]: value,
            });
        }

        // Clear error for this field when it's modified
        if (fieldErrors[name]) {
            setFieldErrors({
                ...fieldErrors,
                [name]: null
            });
        }
    };

    const handleSelectChange = (name, value) => {
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleSelectorInputChange = (e) => {
        const { name, value } = e.target;
        setNewSelector({
            ...newSelector,
            [name]: value,
        });
    };

    const handleEditSelector = (index, field, value) => {
        const updatedSelectors = [...formData.html_selectors];
        updatedSelectors[index] = {
            ...updatedSelectors[index],
            [field]: value
        };
        setFormData({
            ...formData,
            html_selectors: updatedSelectors
        });
    };

    const handleAddSelector = () => {
        if (newSelector.name.trim() === '' || newSelector.selector_path.trim() === '') {
            return; // Don't add empty selectors
        }

        const newSelectorWithId = {
            ...newSelector,
            id: Date.now() // Use timestamp as a simple ID generator
        };

        setFormData({
            ...formData,
            html_selectors: [...formData.html_selectors, newSelectorWithId]
        });

        // Reset the new selector input fields
        setNewSelector({
            name: '',
            selector_path: ''
        });
    };

    const handleRemoveSelector = (id) => {
        setFormData({
            ...formData,
            html_selectors: formData.html_selectors.filter(selector => selector.id !== id)
        });
    };

    const validateForm = () => {
        const errors = {};

        // Note: template_path validation removed since field is now read-only

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate form before submission
        if (!validateForm()) {
            return;
        }

        setIsSaving(true);
        // Clear any existing errors
        setGeneralError(null);

        try {
            // Update the template via API
            await api.templates.update(template.id, formData);
            // Navigate back to organization edit
            navigate(`/organizations/${organization.public_id}/edit`);
        } catch (err) {
            console.error("Failed to update template:", err);

            // For any errors, show a general error at the top
            setGeneralError(err.message);

            setIsSaving(false);
        }
    };

    // Add handler for active toggle
    const handleActiveToggle = async () => {
        // Toggle the active state locally first for responsive UI
        const newStatus = !isActive;
        setIsActive(newStatus);

        try {
            // Update the template status via API
            const result = await api.templates.updateStatus(template.id, newStatus);
            // Update the template with the new status
            setTemplate({
                ...template,
                active: result.active
            });
        } catch (err) {
            // If there's an error, revert the UI state
            console.error("Failed to update template status:", err);
            // Set error state
            setGeneralError(err.message);
            // Revert active state
            setIsActive(!newStatus);
        }
    };

    // Add handler for auto create pages toggle
    const handleAutoCreatePagesToggle = async () => {
        // Toggle the auto create pages state locally first for responsive UI
        const newStatus = !autoCreatePages;
        setAutoCreatePages(newStatus);

        try {
            // Update the template auto create pages setting via API
            const result = await api.templates.updateAutoCreatePages(template.id, newStatus);
            // Update the template with the new setting
            setTemplate({
                ...template,
                auto_create_pages: result.auto_create_pages
            });

            // Also update the formData to keep it in sync
            setFormData({
                ...formData,
                auto_create_pages: result.auto_create_pages
            });
        } catch (err) {
            // If there's an error, revert the UI state
            console.error("Failed to update auto create pages setting:", err);
            // Set error state
            setGeneralError(err.message);
            // Revert auto create pages state
            setAutoCreatePages(!newStatus);
        }
    };

    const handlePurgeCache = async () => {
        try {
            setIsPurgingCache(true);
            setPurgeCacheSuccessMessage(null);
            setPurgeCacheErrorMessage(null);

            // Prepare the identifier - this should be the full domain + path
            // Combine organization's custom domain with template path
            const customDomain = organization?.custom_domain;
            const templatePath = template?.template_path;

            if (!customDomain || !templatePath) {
                throw new Error("Cannot purge cache: Organization custom domain and template path are required");
            }

            // Remove protocol and trailing slash from custom domain if present
            const cleanDomain = customDomain.replace(/^https?:\/\//, '').replace(/\/$/, '');
            // Ensure template path starts without slash and ends with slash
            const cleanPath = templatePath.replace(/^\//, '');

            const templateIdentifier = `${cleanDomain}/${cleanPath}`;

            // Call the API to purge the cache
            const result = await api.serviceBindings.purgeTemplateCache({
                templateIdentifier,
            });

            if (result.success) {
                setPurgeCacheSuccessMessage(result.message || `Cache has been cleared for template: ${template.name}`);
            } else {
                throw new Error(result.error || "Failed to purge cache");
            }
        } catch (error) {
            console.error("Error purging cache:", error);
            setPurgeCacheErrorMessage(error.message);
        } finally {
            setIsPurgingCache(false);
        }
    };

    // Handle dynamic selector prefix change
    const handleDynamicSelectorChange = (e) => {
        const { value } = e.target;
        setFormData({
            ...formData,
            dynamic_selector_prefix: value
        });
    };

    // Handle brand voice origin sites selection change
    const handleBrandVoiceOriginSitesChange = (selectedValues) => {
        const originSitesString = selectedValues.join(',');
        setFormData({
            ...formData,
            brand_voice_origin_sites: originSitesString
        });
    };

    // Check if manual selection should be disabled
    const isManualSelectionDisabled = formData.dynamic_selector_prefix && formData.dynamic_selector_prefix.trim() !== '';

    // Handle copy to clipboard
    const handleCopyToClipboard = async (text, fieldName) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(fieldName);
            // Clear the copied state after 2 seconds
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    if (loading) {
        return (
            <div className="container mx-auto p-4">
                <div className="flex justify-center items-center h-64">
                    <div
                        className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"
                        data-testid="loading-spinner"
                        role="status"
                    >
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!organization || !template) {
        return (
            <div className="container mx-auto p-4">
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>
                        {!organization ? "Organization not found" : "Template not found"}
                    </AlertDescription>
                </Alert>
                <div className="mt-4">
                    <Button variant="outline" onClick={() => navigate(!organization ? '/organizations' : `/organizations/${organization.public_id}/edit`)}>
                        {!organization ? 'Back to Organizations' : 'Back to Organization'}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="container mx-auto p-4">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-3xl font-bold">{template.name}</h1>
                        <p className="text-gray-500">
                            Organization: {organization.name}
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        onClick={() => navigate(`/organizations/${organization.public_id}/edit`)}
                    >
                        Back to Organization
                    </Button>
                </div>

                <form onSubmit={handleSubmit}>
                    <Card className="mb-6">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <div>
                                <CardTitle>Template Details</CardTitle>
                                <CardDescription>
                                    Basic information about the template
                                </CardDescription>
                            </div>
                            <div className="flex flex-col space-y-3">
                                <div className="flex items-center justify-between space-x-2 w-48">
                                    <span className={`text-sm font-medium ${isActive ? 'text-green-600' : 'text-gray-500'}`}>
                                        {isActive ? 'Active' : 'Inactive'}
                                    </span>
                                    <Switch
                                        checked={isActive}
                                        onCheckedChange={handleActiveToggle}
                                        aria-label="Active status"
                                    />
                                </div>
                                <div className="flex items-center justify-between space-x-2 w-48">
                                    <span className={`text-sm font-medium ${autoCreatePages ? 'text-green-600' : 'text-gray-500'}`}>
                                        {autoCreatePages ? 'Auto Create Pages' : 'Manual Pages'}
                                    </span>
                                    <Switch
                                        checked={autoCreatePages}
                                        onCheckedChange={handleAutoCreatePagesToggle}
                                        aria-label="Auto create pages status"
                                    />
                                </div>
                                <div className="flex items-center justify-end w-48 pt-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={handlePurgeCache}
                                        disabled={isPurgingCache || !organization || !template || !(organization && organization.custom_domain && organization.custom_domain.trim() !== '')}
                                        className="w-full"
                                    >
                                        {isPurgingCache ? "Purging..." : "Purge Cache"}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <label htmlFor="name" className="text-sm font-medium">
                                    Template Name
                                </label>
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded-md"
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="unique_key" className="text-sm font-medium">
                                    Template ID
                                </label>
                                <input
                                    id="unique_key"
                                    name="unique_key"
                                    type="text"
                                    value={template?.unique_key || 'Not assigned'}
                                    className="w-full p-2 border rounded-md font-mono text-sm bg-gray-50"
                                    readOnly
                                    disabled
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    This is a unique identifier for your template. It cannot be changed.
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle>LLM Configuration</CardTitle>
                            <CardDescription>
                                Configure how the LLM should process this template
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                <div className="space-y-2">
                                    <label htmlFor="llm_model" className="text-sm font-medium">
                                        LLM Model
                                    </label>
                                    <Select
                                        name="llm_model"
                                        value={formData.llm_model}
                                        onValueChange={(value) => handleSelectChange('llm_model', value)}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder="Select LLM Model" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {llmModelOptions.map(option => (
                                                <SelectItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <label htmlFor="temperature" className="text-sm font-medium">
                                        Temperature
                                    </label>
                                    <Select
                                        name="temperature"
                                        value={formData.temperature.toString()}
                                        onValueChange={(value) => handleSelectChange('temperature', parseFloat(value))}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder="Select Temperature" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {temperatureOptions.map(option => (
                                                <SelectItem key={option.value} value={option.value.toString()}>
                                                    {option.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <label htmlFor="daily_rate_limit" className="text-sm font-medium">
                                        Daily Rate Limit
                                    </label>
                                    <div className="relative w-full">
                                        <input
                                            id="daily_rate_limit"
                                            name="daily_rate_limit"
                                            type="number"
                                            min="0"
                                            step="1"
                                            value={formData.daily_rate_limit}
                                            onChange={handleInputChange}
                                            className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                            placeholder="2000"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        Maximum number of pages that can be created per day
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="llm_context" className="text-sm font-medium">
                                    LLM Context
                                </label>
                                <Textarea
                                    id="llm_context"
                                    name="llm_context"
                                    value={formData.llm_context}
                                    onChange={handleInputChange}
                                    className="min-h-[150px]"
                                    placeholder="Enter the context/system prompt for the LLM"
                                />
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="llm_prompt" className="text-sm font-medium">
                                    LLM Prompt
                                </label>
                                <Textarea
                                    id="llm_prompt"
                                    name="llm_prompt"
                                    value={formData.llm_prompt}
                                    onChange={handleInputChange}
                                    className="min-h-[100px]"
                                    placeholder="Enter the prompt template with variables in {{double_braces}}"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium">
                                    Brand Voice Origin Sites
                                </label>
                                {isBrandVoiceLoading ? (
                                    <div className="flex items-center py-4">
                                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-gray-900 mr-2"></div>
                                        <span className="text-sm text-gray-600">Loading brand voice data...</span>
                                    </div>
                                ) : originSites.length > 0 ? (
                                    <div className="space-y-3">
                                        <p className="text-xs text-gray-500">
                                            Select which brand voice documents to include when generating content for this template.
                                        </p>
                                        <div className="grid grid-cols-1 gap-3 max-h-40 overflow-y-auto border rounded-md p-3">
                                            {originSites.map((site) => {
                                                const isSelected = formData.brand_voice_origin_sites
                                                    .split(',')
                                                    .filter(s => s.trim())
                                                    .includes(site.origin_site);

                                                return (
                                                    <div key={site.origin_site} className="flex items-center space-x-2">
                                                        <Checkbox
                                                            id={`origin-site-${site.origin_site}`}
                                                            checked={isSelected}
                                                            onCheckedChange={(checked) => {
                                                                const currentSites = formData.brand_voice_origin_sites
                                                                    .split(',')
                                                                    .filter(s => s.trim());

                                                                let newSites;
                                                                if (checked) {
                                                                    newSites = [...currentSites, site.origin_site];
                                                                } else {
                                                                    newSites = currentSites.filter(s => s !== site.origin_site);
                                                                }

                                                                handleBrandVoiceOriginSitesChange(newSites);
                                                            }}
                                                        />
                                                        <Label
                                                            htmlFor={`origin-site-${site.origin_site}`}
                                                            className="text-sm font-normal cursor-pointer flex-1"
                                                        >
                                                            <div className="flex justify-between">
                                                                <span>{site.origin_site}</span>
                                                            </div>
                                                        </Label>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-4 text-gray-500 border rounded-md">
                                        <p className="text-sm">No brand voice documents found for this organization.</p>
                                        <p className="text-xs mt-1">Add documents in the Organization settings to enable brand voice integration.</p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle>Page URLs</CardTitle>
                            <CardDescription>
                                View the URLs for this template (click to copy)
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <label htmlFor="master_html_url" className="text-sm font-medium">
                                    Master HTML Template URL
                                </label>
                                <div className="relative">
                                    <input
                                        id="master_html_url"
                                        name="master_html_url"
                                        type="url"
                                        value={formData.master_html_url}
                                        className="w-full p-2 pr-10 border rounded-md bg-gray-50 cursor-pointer font-mono text-sm"
                                        readOnly
                                        onClick={() => handleCopyToClipboard(formData.master_html_url, 'master_html_url')}
                                        title="Click to copy"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleCopyToClipboard(formData.master_html_url, 'master_html_url')}
                                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                        title="Copy to clipboard"
                                    >
                                        {copiedField === 'master_html_url' ? (
                                            <Check className="h-4 w-4 text-green-600" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    This is the URL of the page that will be used as a template.
                                </p>
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="template_path" className="text-sm font-medium">
                                    Template Path
                                </label>
                                <div className="flex items-center">
                                    <div className="bg-gray-100 px-3 py-2 border border-r-0 rounded-l-md text-gray-500 font-mono text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                                        {organization.custom_domain || 'https://domain.com'}
                                    </div>
                                    <div className="relative flex-1">
                                        <input
                                            id="template_path"
                                            name="template_path"
                                            type="text"
                                            value={formData.template_path}
                                            className="w-full p-2 pr-10 border border-l-0 rounded-r-md bg-gray-50 cursor-pointer font-mono text-sm"
                                            readOnly
                                            onClick={() => {
                                                const fullUrl = `${organization.custom_domain || 'https://domain.com'}${formData.template_path}`;
                                                handleCopyToClipboard(fullUrl, 'template_path');
                                            }}
                                            title="Click to copy full URL"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const fullUrl = `${organization.custom_domain || 'https://domain.com'}${formData.template_path}`;
                                                handleCopyToClipboard(fullUrl, 'template_path');
                                            }}
                                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                            title="Copy full URL to clipboard"
                                        >
                                            {copiedField === 'template_path' ? (
                                                <Check className="h-4 w-4 text-green-600" />
                                            ) : (
                                                <Copy className="h-4 w-4" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    The path where generated pages will be publicly accessible. Click to copy the full URL.
                                </p>
                            </div>

                            {/* Noindex Toggle */}
                            <div className="space-y-2 pt-4 border-t">
                                <div className="flex items-center justify-between">
                                    <div className="space-y-0.5">
                                        <label htmlFor="noindex" className="text-sm font-medium">
                                            Exclude from Search Engines
                                        </label>
                                        <p className="text-xs text-muted-foreground">
                                            When enabled, generated pages will include a meta robots tag (noindex, nofollow) to prevent search engines from indexing them.
                                        </p>
                                    </div>
                                    <Switch
                                        id="noindex"
                                        checked={noindex}
                                        onCheckedChange={(checked) => {
                                            setNoindex(checked);
                                            setFormData(prev => ({ ...prev, noindex: checked }));
                                        }}
                                        aria-label="Exclude from search engines"
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle>HTML Selectors</CardTitle>
                            <CardDescription>
                                Define selectors to target specific elements in the HTML template
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <Tabs defaultValue={selectorMode} onValueChange={setSelectorMode} className="w-full">
                                <TabsList className="grid w-full grid-cols-2 mb-4">
                                    <TabsTrigger value="manual">Manual Selection</TabsTrigger>
                                    <TabsTrigger value="dynamic">Dynamic Selection</TabsTrigger>
                                </TabsList>

                                <TabsContent value="manual" className="space-y-4">
                                    {isManualSelectionDisabled && (
                                        <>
                                            <Alert variant="warning">
                                                <AlertCircle className="h-4 w-4" />
                                                <AlertTitle>Manual Selection Disabled</AlertTitle>
                                                <AlertDescription>
                                                    Manual selection is disabled because a dynamic selector prefix is configured.
                                                    To use manual selection, clear the dynamic selector prefix in the "Dynamic Selection" tab.
                                                </AlertDescription>
                                            </Alert>
                                        </>
                                    )}

                                    {formData.html_selectors.length > 0 && (
                                        <div className="border rounded-md overflow-hidden">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Name
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Selector Path
                                                        </th>
                                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Actions
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {formData.html_selectors.map((selector, index) => (
                                                        <tr key={selector.id}>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <input
                                                                    type="text"
                                                                    value={selector.name}
                                                                    onChange={(e) => handleEditSelector(index, 'name', e.target.value)}
                                                                    className={`w-full p-1 border rounded-md ${isManualSelectionDisabled ? 'bg-gray-100 opacity-60' : ''}`}
                                                                    disabled={isManualSelectionDisabled}
                                                                />
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <input
                                                                    type="text"
                                                                    value={selector.selector_path}
                                                                    onChange={(e) => handleEditSelector(index, 'selector_path', e.target.value)}
                                                                    className={`w-full p-1 border rounded-md ${isManualSelectionDisabled ? 'bg-gray-100 opacity-60' : ''}`}
                                                                    placeholder="e.g., #header > h1"
                                                                    disabled={isManualSelectionDisabled}
                                                                />
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-right">
                                                                <Button
                                                                    variant="destructive"
                                                                    size="sm"
                                                                    onClick={() => handleRemoveSelector(selector.id)}
                                                                    disabled={isManualSelectionDisabled}
                                                                >
                                                                    Remove
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    <div className={`border rounded-md p-4 ${isManualSelectionDisabled ? 'bg-gray-100' : 'bg-gray-50'}`}>
                                        <h3 className="text-lg font-medium mb-3">Add New Selector</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                            <div className="space-y-2">
                                                <label htmlFor="selectorName" className="text-sm font-medium">
                                                    Name
                                                </label>
                                                <input
                                                    id="selectorName"
                                                    name="name"
                                                    type="text"
                                                    value={newSelector.name}
                                                    onChange={handleSelectorInputChange}
                                                    className={`w-full p-2 border rounded-md ${isManualSelectionDisabled ? 'bg-gray-200 opacity-60' : ''}`}
                                                    placeholder="e.g., Headline Text"
                                                    disabled={isManualSelectionDisabled}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label htmlFor="selectorPath" className="text-sm font-medium">
                                                    Selector Path
                                                </label>
                                                <input
                                                    id="selectorPath"
                                                    name="selector_path"
                                                    type="text"
                                                    value={newSelector.selector_path}
                                                    onChange={handleSelectorInputChange}
                                                    className={`w-full p-2 border rounded-md ${isManualSelectionDisabled ? 'bg-gray-200 opacity-60' : ''}`}
                                                    placeholder="e.g., #main-content > .headline"
                                                    disabled={isManualSelectionDisabled}
                                                />
                                            </div>
                                        </div>
                                        <Button
                                            type="button"
                                            onClick={handleAddSelector}
                                            disabled={!newSelector.name || !newSelector.selector_path || isManualSelectionDisabled}
                                            className={isManualSelectionDisabled ? 'opacity-60' : ''}
                                        >
                                            Add Selector
                                        </Button>
                                    </div>

                                    {formData.html_selectors.length === 0 && !isManualSelectionDisabled && (
                                        <div className="text-center py-4 text-gray-500">
                                            No selectors added yet. Add your first selector above.
                                        </div>
                                    )}
                                </TabsContent>

                                <TabsContent value="dynamic" className="space-y-4">
                                    <div className="border rounded-md p-4">
                                        <div className="space-y-4">
                                            <div>
                                                <h3 className="text-lg font-medium mb-3">Dynamic ID Selector Prefix</h3>
                                                <p className="text-gray-500 mb-4">
                                                    Enter a prefix that will be used to automatically select all HTML elements with IDs starting with this prefix.
                                                    For example, entering "dyn_" will select elements with IDs like "dyn_headline", "dyn_description", etc.
                                                </p>
                                            </div>
                                            <div className="space-y-2">
                                                <label htmlFor="dynamicPrefix" className="text-sm font-medium">
                                                    ID Prefix
                                                </label>
                                                <input
                                                    id="dynamicPrefix"
                                                    name="dynamic_selector_prefix"
                                                    type="text"
                                                    value={formData.dynamic_selector_prefix}
                                                    onChange={handleDynamicSelectorChange}
                                                    className="w-full p-2 border rounded-md"
                                                    placeholder="e.g., dyn_"
                                                />
                                            </div>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger className="flex items-center underline cursor-help">
                                                                <AlertCircle className="h-4 w-4 mr-1" />
                                                                How does this work?
                                                            </TooltipTrigger>
                                                            <TooltipContent className="max-w-xs p-3">
                                                                <p>
                                                                    Dynamic selection automatically finds all HTML elements with IDs starting with your specified prefix.
                                                                    The element ID after the prefix will be used as the selector name.
                                                                    <br /><br />
                                                                    Example: With prefix "dyn_", an element with ID "dyn_headline" will be selected with the name "headline".
                                                                </p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </TabsContent>
                            </Tabs>
                        </CardContent>
                    </Card>

                    {generalError && (
                        <Alert variant="destructive" className="mb-4">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>Error</AlertTitle>
                            <AlertDescription>{generalError}</AlertDescription>
                        </Alert>
                    )}

                    {purgeCacheSuccessMessage && (
                        <Alert variant="success" className="mb-4">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>Success</AlertTitle>
                            <AlertDescription>{purgeCacheSuccessMessage}</AlertDescription>
                        </Alert>
                    )}
                    {purgeCacheErrorMessage && (
                        <Alert variant="destructive" className="mb-4">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>Purge Error</AlertTitle>
                            <AlertDescription>{purgeCacheErrorMessage}</AlertDescription>
                        </Alert>
                    )}

                    <div className="flex justify-end gap-4">
                        <Button
                            variant="outline"
                            type="button"
                            onClick={() => navigate(`/organizations/${organization.public_id}/edit`)}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSaving}>
                            Save Template
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default TemplateEdit; 
